/**
 * Partner referral code — apply it to the REAL cart.
 *
 * Why this exists
 * ---------------
 * A creator's Collabs link (rbrnsupps.com/THEIRCODE) 301s to
 * /discount/THEIRCODE?...&redirect=/products/… which 302s to the product page,
 * leaving a `discount_code` cookie behind. That cookie is a promise to CHECKOUT,
 * not cart state: Shopify never prices a code discount into the cart, so
 * cart.cart_level_discount_applications stays empty and the drawer would show a
 * full-price total while the product page advertises the partner price.
 *
 * Posting the code to /cart/update.js fixes that properly. Shopify then owns the
 * math: the drawer shows real discounted totals, the code renders as a pill via
 * the markup cart-summary.liquid already has, and checkout matches exactly.
 *
 * It is also the only validity check available. Shopify sets the cookie for
 * INVALID codes too — it does not validate at the /discount/ hop — so a revoked
 * partner's link would otherwise advertise a price we cannot honour. The
 * response's `applicable` flag is the truth, and a false hides the product-page
 * treatment again.
 *
 * Deliberately NOT an edit to cart-discount.js: that is stock Shopify with one
 * RBRN modification already in it, and its applyDiscount is bound as a
 * declarative form-submit handler with no programmatic entry point.
 */

const REF_KEY = 'rbrn_partner_ref';
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/** Codes already on the cart, read the same way cart-discount.js does. */
function existingDiscounts() {
  return Array.from(document.querySelectorAll('.cart-discount__pill'))
    .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : null))
    .filter((code) => typeof code === 'string' && code.length > 0);
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function sessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null; // private mode
  }
}

function sessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    /* private mode — the URL param still covers the landing page */
  }
}

/**
 * The gate: a Collabs tracking id AND a discount cookie.
 * dt_id alone proves the visitor came from an affiliate link; the cookie carries
 * the code itself. Either on its own is not enough — a code applied from, say, a
 * back-in-stock email has no dt_id and must not be treated as a partner offer.
 */
function referralContext() {
  const fromUrl = new URLSearchParams(window.location.search).get('dt_id');
  if (fromUrl) sessionSet(REF_KEY, fromUrl);
  const dtId = fromUrl || sessionGet(REF_KEY);
  const code = readCookie('discount_code');
  return dtId && code ? { dtId, code } : null;
}

/** Pull the product-page treatment back down when the code turns out to be dead. */
function retractTreatment() {
  document
    .querySelectorAll('[data-rbrn-referral-banner],[data-rbrn-referral-row],[data-rbrn-referral-onetime]')
    .forEach((el) => {
      el.hidden = true;
    });
  // Row 2 is a swap: the referral today/then pair replaces the plain was/now
  // pair in the same slot. Put the originals back or the card loses its prices
  // entirely when a code turns out to be dead.
  document.querySelectorAll('[data-rbrn-referral-hide]').forEach((el) => {
    el.hidden = false;
  });
  document
    .querySelectorAll('[data-rbrn-referral-active]')
    .forEach((el) => el.removeAttribute('data-rbrn-referral-active'));

  // The subscription widget writes a two-part "charged X today, then Y" line
  // while the offer is live. Tell it to put the plain sentence back, or the
  // disclaimer keeps promising a discount the cart just rejected.
  document.dispatchEvent(new CustomEvent('rbrn:referral-changed'));
}

let inFlight = false;

/**
 * A logged-in partner who arrives through a referral link (their own or another
 * creator's) must not stack the 10% first-order code on their 25% partner plan.
 * Skipping the cart post is not enough: Shopify carries the discount_code cookie
 * to checkout on its own. So withdraw the code everywhere it lives — sessionStorage,
 * the cookie, and the cart if it is already there — leaving any other code alone.
 * The partner plan itself is only offered to partners logged in on the storefront,
 * which is when window.RBRN_CUSTOMER_IS_PARTNER is set (scripts.liquid).
 */
async function withdrawForPartner(context) {
  try {
    sessionStorage.removeItem(REF_KEY);
  } catch (error) {
    /* private mode */
  }
  // Expire every variant Shopify may have set: host-only and both domain forms.
  const host = window.location.hostname;
  ['', `; domain=${host}`, `; domain=.${host.replace(/^www\./, '')}`].forEach((domain) => {
    document.cookie = `discount_code=; Max-Age=0; path=/${domain}`;
  });
  retractTreatment();

  const existing = existingDiscounts();
  if (!existing.includes(context.code)) return;
  const remaining = existing.filter((code) => code !== context.code);
  try {
    const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: remaining.join(',') }),
    });
    if (response.ok) {
      document.dispatchEvent(new CustomEvent(CART_LINES_UPDATE, { detail: { source: 'rbrn-partner-referral' } }));
    }
  } catch (error) {
    /* the cookie is already gone, so checkout will not re-add it */
  }
}

async function applyReferralCode() {
  if (inFlight) return;

  const context = referralContext();
  if (!context) return;

  if (window.RBRN_CUSTOMER_IS_PARTNER === true) {
    inFlight = true;
    try {
      await withdrawForPartner(context);
    } finally {
      inFlight = false;
    }
    return;
  }

  // ⚠️ Never attempt this against an empty cart. Shopify answers `applicable:
  // false` for a perfectly valid code when there is nothing for it to apply to,
  // and treating that as a rejection retracts the product-page treatment on
  // first load — before the visitor has added anything. The rejection signal is
  // only meaningful once the cart has lines.
  let cart;
  try {
    const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) return;
    cart = await cartResponse.json();
  } catch (error) {
    return;
  }
  if (!cart || !cart.item_count) return;

  const existing = existingDiscounts();
  if (existing.includes(context.code)) return; // already on the cart

  // Append, never clobber: `discount` REPLACES the whole set, so posting ours
  // alone would silently drop a code the customer typed themselves.
  const allCodes = [...existing, context.code];

  inFlight = true;
  try {
    const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: allCodes.join(',') }),
    });
    if (!response.ok) return;

    const data = await response.json();
    const rejected = (data.discount_codes || []).some(
      (discount) => discount.code === context.code && discount.applicable === false
    );

    if (rejected) {
      retractTreatment();
      return;
    }

    // Let the cart UI repaint with Shopify's numbers.
    document.dispatchEvent(new CustomEvent(CART_LINES_UPDATE, { detail: { source: 'rbrn-partner-referral' } }));
  } catch (error) {
    /* Network failure is not worth surfacing: the cookie still carries the code
       to checkout, so the customer is not actually losing the discount. */
  } finally {
    inFlight = false;
  }
}

document.addEventListener(CART_LINES_UPDATE, (event) => {
  // Ignore the event we dispatch ourselves, or this recurses.
  if (event.detail?.source === 'rbrn-partner-referral') return;
  applyReferralCode();
});

// Cover the case where the visitor already had items before arriving on the link.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyReferralCode, { once: true });
} else {
  applyReferralCode();
}
